import './layout.scss';
import '../menu/menu.scss';
import '../input/dropdownInput-v1-1.scss';

import { observer } from 'mobx-react-lite';
import React, { Dispatch, useState, useEffect } from 'react';
import { usePopper } from 'react-popper';
import { Link } from './link';
import classNames from 'classnames';

import Routes, { BackRoutes } from '../../routes';
import { useOutsideClick, useDelayedUnmount } from '../../components/hooks';
import { ChevronDownSVGIcon } from '../svg';

type UserAccountDropdownProps = {
  showProfile: boolean;
  showHelp: boolean;
  isOpened: boolean;
  showLogout: boolean;
  onLogoutButtonClick?: React.MouseEventHandler;
  onHelpButtonClick?: React.MouseEventHandler;
  setIsOpened: Dispatch<boolean>;
};

export const UserAccountDropdown = observer(({
  isOpened,
  setIsOpened,
  showProfile,
  showHelp,
  showLogout,
  onLogoutButtonClick,
  onHelpButtonClick
}: UserAccountDropdownProps) => {
  const [popperElement, setPopperElement] = useState(null);
  const [anchorElement, setAnchorElement] = useState(null);
  const [arrowElement] = useState(null);
  const [forceClose, setForceClose] = useState(false);
  const { shouldRender, isAnimating } = useDelayedUnmount(isOpened && !forceClose, forceClose ? 0 : 400);

  // Listen for custom event to close this menu when another opens
  useEffect(() => {
    const handleCloseAll = () => {
      if (isOpened) {
        setForceClose(true);
        setIsOpened(false);
      }
    };
    document.addEventListener('dropdown:close-all', handleCloseAll);
    return () => document.removeEventListener('dropdown:close-all', handleCloseAll);
  }, [isOpened, setIsOpened]);

  // Reset forceClose when menu is fully closed
  useEffect(() => {
    if (!isOpened && forceClose) {
      setForceClose(false);
    }
  }, [isOpened, forceClose]);

  const { styles, attributes } = usePopper(anchorElement, popperElement, {
    placement: 'bottom-end',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom'],
        },
      },
    ],
  });

  useOutsideClick(popperElement, () => setIsOpened(false));

  return (
    <>
      <button
        className={
          'btn btn-core btn-icon small ' +
          (isOpened ? 'is-open' : '')
        }
        ref={setAnchorElement as React.Ref<HTMLButtonElement>}
        onClick={(evt) => {
          if (!isOpened) {
            document.dispatchEvent(new CustomEvent('dropdown:close-all'));
          }
          setIsOpened(!isOpened);
          evt.stopPropagation();
        }}>
        <span className="icon">
          <ChevronDownSVGIcon />
        </span>
      </button>

      {shouldRender && (
        <div
          className={classNames('dropdown-menu menu-popup popup menu-animate', isAnimating)}
          ref={setPopperElement as React.Ref<HTMLDivElement>}
          style={styles.popper}
          {...attributes.popper}>
          <div className="popup-arrow" style={styles.arrow} />
          <div
            className="menu-items"
            onClick={() => setIsOpened(false)}>

            {showProfile && (
              <div className="menu-item">
                <Link
                  to={
                    BackRoutes[Routes.account].route({ currentPath: document.location.pathname })
                  }
                  className="label">
                  <span className="label-text">Profile</span>
                </Link>
              </div>
            )}

            {showHelp && onHelpButtonClick && (
              <div className="menu-item open-tutorial-menu-item">
                <div
                  className="label"
                  onClick={onHelpButtonClick}>
                  <span className="label-text">Help</span>
                </div>
              </div>
            )}

            {showLogout && onLogoutButtonClick && (
              <div className="menu-item">
                <div
                  className="label"
                  onClick={onLogoutButtonClick}>
                  <span className="label-text">Logout</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
});